import { format, getMonth, getYear } from 'date-fns'
import React, { useEffect } from 'react'
import scriptLoader from 'react-async-script-loader'
import { connect } from 'react-redux'
import { destroy } from 'redux-form'

import CheckoutForm from '../components/checkout/CheckoutForm'
import DialogDeliveryInstructions from '../components/checkout/DialogDeliveryInstructions'
import { Article, SpinnerCard } from '../components/common/Layout'
import { ValidationModal } from '../components/place-order'
import { useStateValue } from '../providers/ProviderCheckout'
import { loadCheckoutCartStart } from '../redux/modules/cart'
import {
  applyCoupon,
  applyCredit,
  checkoutStart,
  clearError,
  loadAvailableProgram,
  loadCreditCardsStart,
} from '../redux/modules/checkout'
import { loadAvailableCreditsStart } from '../redux/modules/creditHistory'
import { loadDeliveryDropoffStart } from '../redux/modules/delivery'
import {
  loadLocationStart,
  toggleDeliveryInstructionsDialog,
  updateUserSavedLocationStart,
} from '../redux/modules/location'
import { loadProfileStart } from '../redux/modules/profile'
import {
  findSelectedCreditCard,
  selectCheckoutUser,
  selectCurrentSavedLocation,
  selectProfile,
  selectSuggestedCredits,
  selectUserLocationId,
} from '../redux/modules/selectors'
import { HEARTLAND_SCRIPT_URL } from '../services/heartland'

const PageCheckout = ({
  applyCoupon,
  applyCredit,
  availableCredits,
  checkoutCart,
  checkoutError,
  checkoutStart,
  checkoutUser,
  clearError,
  couponError,
  couponUsage,
  creditCards,
  creditCardsError,
  creditError,
  creditUsage,
  curSavedLocation,
  deliveryError,
  destroy,
  isAvailableCreditsLoading,
  isCheckoutCartLoading,
  isCouponApplying,
  isCreditApplying,
  isCreditCardsLoading,
  isCurrentUserLoading,
  isLoadingProfile,
  isLoadingProgram,
  isScriptLoaded,
  isUpdatingSavedLocation,
  loadAvailableCreditsStart,
  loadAvailableProgram,
  loadCheckoutCartStart,
  loadCreditCardsStart,
  loadDeliveryDropoffStart,
  loadLocationStart,
  match: {
    params: { dropoffId = 0, locationId = 0 },
  },
  profile,
  programInfo,
  selectedCreditCard,
  showDeliveryInstructionsDialog,
  storedCurrencyUsage,
  subscriptionDetails,
  subscriptionPromoPreference,
  suggestedCredits,
  toggleDeliveryInstructionsDialog,
  updateSavedLocationError,
  updateUserSavedLocationStart,
  watchCheckoutSuccess,
}) => {
  const [{ subscription }] = useStateValue()

  // runs once on mount, and returned function runs on unmount
  useEffect(() => {
    loadCreditCardsStart()
    loadAvailableCreditsStart()
    return () => {
      clearError()
      destroy('checkout')
    }
  }, [clearError, destroy, loadAvailableCreditsStart, loadCreditCardsStart])

  // when locationId updates
  useEffect(() => {
    const numId = Number(locationId)

    if (numId) {
      loadLocationStart(numId)
    }
  }, [locationId, loadLocationStart])

  // when dropoffId updates
  useEffect(() => {
    const numId = Number(dropoffId)
    if (numId) {
      loadDeliveryDropoffStart(numId)
    }
  }, [dropoffId, loadCheckoutCartStart, loadDeliveryDropoffStart, subscription.value])

  // When subscription value changes
  useEffect(() => {
    const numId = Number(dropoffId)
    if (numId) {
      loadCheckoutCartStart(numId, subscription.value)
    }
  }, [dropoffId, loadCheckoutCartStart, subscription.value])

  // when orderDate changes
  useEffect(() => {
    if (checkoutCart.orderDate) {
      loadAvailableProgram(format(checkoutCart.orderDate, 'YYYY-MM-DD'))
    }
  }, [checkoutCart.orderDate, loadAvailableProgram])

  locationId = Number(locationId)
  const now = new Date()

  if (deliveryError) {
    const serverMessage = deliveryError.response ? deliveryError.response.data : ''
    return <ValidationModal locationId={locationId} serverMessage={serverMessage} />
  } else {
    return (
      <Article>
        <SpinnerCard
          spinnerActive={
            isCreditCardsLoading ||
            isCurrentUserLoading ||
            isLoadingProfile ||
            isLoadingProgram ||
            !isScriptLoaded
          }
        >
          <CheckoutForm
            checkoutStart={checkoutStart}
            coupon={couponUsage}
            credit={creditUsage}
            destroyOnUnmount={false}
            initialValues={{
              SelectedCard:
                checkoutCart.orderTotal === 0
                  ? null
                  : creditCards.length === 1
                  ? creditCards[0].ccProfileId
                  : creditCards.length > 0
                  ? null
                  : -1,
              User: { ...checkoutUser, ...profile },
              cc: { expMonth: getMonth(now) + 1, expYear: getYear(now) },
              saveCard: true,
            }}
            program={programInfo}
            user={{ ...checkoutUser, ...profile }}
            {...{
              applyCoupon,
              applyCredit,
              availableCredits,
              checkoutCart,
              checkoutError,
              couponError,
              creditCards,
              creditCardsError,
              creditError,
              curSavedLocation,
              dropoffId,
              isAvailableCreditsLoading,
              isCheckoutCartLoading,
              isCouponApplying,
              isCreditApplying,
              isCreditCardsLoading,
              isLoadingProgram,
              loadCheckoutCartStart,
              selectedCreditCard,
              storedCurrencyUsage,
              subscriptionDetails,
              subscriptionPromoPreference,
              suggestedCredits,
              toggleDeliveryInstructionsDialog,
              watchCheckoutSuccess,
            }}
          />
        </SpinnerCard>
        <DialogDeliveryInstructions
          curSavedLocation={curSavedLocation}
          isUpdatingSavedLocation={isUpdatingSavedLocation}
          showDeliveryInstructionsDialog={showDeliveryInstructionsDialog}
          toggleDeliveryInstructionsDialog={toggleDeliveryInstructionsDialog}
          updateSavedLocationError={updateSavedLocationError}
          updateUserSavedLocationStart={updateUserSavedLocationStart}
        />
      </Article>
    )
  }
}

const mapStateToProps = state => {
  const {
    checkoutError,
    checkoutSuccess,
    couponError,
    creditCards,
    creditCardsError,
    creditError,
    isCouponApplying,
    isCreditApplying,
    isCreditCardsLoading,
    isLoadingProgram,
    programInfo,
    watchCheckoutSuccess,
  } = state.checkout
  const deliveryError = state.delivery.error
  const { currentUser, isCurrentUserLoading } = state.user
  const { availableCredits, isAvailableCreditsLoading } = state.creditHistory
  const {
    checkoutCart,
    couponUsage,
    creditUsage,
    isCheckoutCartLoading,
    loadCheckoutCartSuccess,
    storedCurrencyUsage,
    subscriptionDetails,
  } = state.cart
  const {
    isUpdatingSavedLocation,
    showDeliveryInstructionsDialog,
    updateSavedLocationError,
  } = state.location
  const { isLoadingProfile } = state.profile
  const profile = selectProfile(state)
  const selectedCreditCard = findSelectedCreditCard(state)
  const locationId = selectUserLocationId(state)
  const curSavedLocation = selectCurrentSavedLocation(state)
  const subscriptionPromoPreference = state?.subscriptions?.subscriptionPromoPreference

  return {
    availableCredits,
    checkoutCart,
    checkoutError,
    checkoutSuccess,
    checkoutUser: selectCheckoutUser(state),
    couponError,
    couponUsage,
    creditCards,
    creditCardsError,
    creditError,
    creditUsage,
    curSavedLocation,
    currentUser,
    deliveryError,
    isAvailableCreditsLoading,
    isCheckoutCartLoading,
    isCouponApplying,
    isCreditApplying,
    isCreditCardsLoading,
    isCurrentUserLoading,
    isLoadingProfile,
    isLoadingProgram,
    isUpdatingSavedLocation,
    loadCheckoutCartSuccess,
    locationId,
    profile,
    programInfo,
    selectedCreditCard,
    showDeliveryInstructionsDialog,
    storedCurrencyUsage,
    subscriptionDetails,
    subscriptionPromoPreference,
    suggestedCredits: selectSuggestedCredits(state),
    updateSavedLocationError,
    watchCheckoutSuccess,
  }
}

const mapDispatchToProps = {
  applyCoupon,
  applyCredit,
  checkoutStart,
  clearError,
  destroy,
  loadAvailableCreditsStart,
  loadAvailableProgram,
  loadCheckoutCartStart,
  loadCreditCardsStart,
  loadDeliveryDropoffStart,
  loadLocationStart,
  loadProfileStart,
  toggleDeliveryInstructionsDialog,
  updateUserSavedLocationStart,
}
export default scriptLoader(HEARTLAND_SCRIPT_URL)(
  connect(mapStateToProps, mapDispatchToProps)(PageCheckout),
)
